/**
 * Resolve a franchise from a URL `franchise` slug.
 *
 * Slugs are only unique per restaurant in core (`@Unique(["slug", "restaurant"])`
 * on Franchise), but the guest payload concatenates the partner restaurants'
 * franchises into one list (restaurantService.getPartnerRestaurantDetails), so
 * two live branches can legitimately share a slug — Vasko's Korba (r121) and
 * Lebanon St. (r684) are both "B02". A plain `find` always returns the first
 * one, which silently swapped the branch the guest picked in the welcome
 * drawer: the drawer committed Lebanon St. to Redux, pushed `?franchise=B02`,
 * and every URL→Redux sync then resolved B02 back to Korba.
 *
 * When several franchises match the slug, prefer the one already committed
 * (matched by id); with a single match this is exactly the old `find`.
 */
export const findFranchiseBySlug = <
  T extends { id: number; slug?: string | null }
>(
  franchises: T[] | undefined | null,
  slug: string | undefined | null,
  preferred?: { id?: number | null } | null
): T | undefined => {
  if (!franchises?.length) {
    return undefined;
  }

  const matches = franchises.filter((franchise) => franchise.slug === slug);

  if (matches.length <= 1) {
    return matches[0];
  }

  return (
    matches.find((franchise) => franchise.id === preferred?.id) ?? matches[0]
  );
};
